import { useState, useEffect, useRef } from 'react';
import PageHeader from '../components/PageHeader';
import { supabase } from '../lib/supabase';
import { API_BASE } from '../apiBase';
import { syncDeductForOrder, getSyncMode, setSyncMode } from '../lib/syncInventory';

const colors = {
  bg: '#f0f1f3',
  cardBg: '#ffffff',
  navy: '#071a52',
  greenDark: '#5cb83c',
  textPrimary: '#1a1a2e',
  textSecondary: '#6b7280',
  border: '#e2e4e8',
};

const ORDER_COLORS = ['#f472b6', '#60a5fa', '#4ade80', '#fb923c', '#a78bfa', '#f87171', '#34d399', '#fbbf24'];

const stages = [
  { key: 'Pending', accent: '#d97706' },
  { key: 'Pulling', accent: '#2563eb' },
  { key: 'Packed',  accent: '#7c3aed' },
  { key: 'Shipped', accent: '#16a34a' },
];

const API = API_BASE;

const DEFAULT_MESSAGE =
`Hello <BUYERNAME>!

Your BrickLink order #<ORDERID> was packed today (<CURRENTDATE>) and will be dropped off <TOMORROWDATE>. It should reach you soon!

Thanks so much for choosing JayKay Bricks to serve your LEGO needs! We hope this transaction has been as smooth for you as it has been for us and that we can cross each other's paths in the future. If something is wrong with your order, please contact us ASAP so we can work everything out. If all is well, we would certainly appreciate it if you could take a moment to leave our store positive feedback.

Thanks so much, and happy building!

Sincerely,

Jim and Kaitlyn
of JayKay Bricks
https://store.bricklink.com/JayKayBricks`;

const DEFAULT_FEEDBACK = 'Thanks so much!! Happy building!!';

function resolveTemplate(template, { buyerName, orderId, pieces, total }) {
  const today = new Date();
  const day = today.getDay();
  const shipDate = new Date(today);
  if (day === 5) shipDate.setDate(today.getDate() + 1);
  else if (day === 6) shipDate.setDate(today.getDate() + 2);
  else if (day === 0) shipDate.setDate(today.getDate() + 1);
  else shipDate.setDate(today.getDate() + 1);
  const fmt = d => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  return template
    .replace(/<BUYERNAME>/g, buyerName || '')
    .replace(/<ORDERID>/g, orderId || '')
    .replace(/<CURRENTDATE>/g, fmt(today))
    .replace(/<TOMORROWDATE>/g, fmt(shipDate))
    .replace(/<PIECES>/g, pieces || '')
    .replace(/<TOTAL>/g, total || '');
}

function statusToStage(blStatus) {
  if (!blStatus) return 'Pending';
  const s = blStatus.toUpperCase();
  if (s === 'PENDING' || s === 'PROCESSING' || s === 'PAID') return 'Pending';
  if (s === 'UPDATED') return 'Pulling';
  if (s === 'PACKED' || s === 'READY') return 'Packed';
  return 'Pending';
}

// Strip overflow baggie notations like "AA002 +BT1" → "AA002"
function getPrimaryBin(remark) {
  if (!remark) return '';
  return remark.split(/\s*\+/)[0].trim();
}

function getImageUrl(item) {
  if (item.boImage) return item.boImage;
  const isMinifig = item.type === 'M' || item.type === 'MINIFIG' ||
    (item.partNo || '').toLowerCase().startsWith('fig-');
  if (isMinifig) return `https://img.bricklink.com/ItemImage/MN/0/${item.partNo}.png`;
  return `https://img.bricklink.com/ItemImage/PN/${item.colorId}/${item.partNo}.png`;
}

function Orders() {
  const [screen, setScreen] = useState('list');
  const [orders, setOrders] = useState([]);
  const [orderItems, setOrderItems] = useState({});
  const [loadingItems, setLoadingItems] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPickRun, setLoadingPickRun] = useState(false);
  const [activeStage, setActiveStage] = useState('all');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [pickQueue, setPickQueue] = useState([]);
  const [pickIndex, setPickIndex] = useState(0);
  const [picked, setPicked] = useState(new Set());
  const [tracking, setTracking] = useState({});
  const [realNames, setRealNames] = useState({});      // order.id -> buyer's real name
  const [buyerEmails, setBuyerEmails] = useState({});  // order.id -> buyer's email
  const [trackingImport, setTrackingImport] = useState(null); // CSV import result summary
  const [fulfilling, setFulfilling] = useState(false);
  const [fulfillResults, setFulfillResults] = useState(null);
  const blLotMapRef = useRef(null);
  const [reconcile, setReconcile] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileRev, setReconcileRev] = useState(null);
  const [reconcilingRev, setReconcilingRev] = useState(false);
  const [reviewedLots, setReviewedLots] = useState(new Set());
  const [binAuditDates, setBinAuditDates] = useState({});
  const [customItems, setCustomItems] = useState([]);       // saved custom-pick lines
  const [customMode, setCustomMode] = useState(false);      // current pick run is a custom pick
  const [customPart, setCustomPart] = useState('');
  const [customColor, setCustomColor] = useState('');
  const [customQty, setCustomQty] = useState('1');
  const [customPaste, setCustomPaste] = useState('');
  const [customBusy, setCustomBusy] = useState(false);
  const [savedSession, setSavedSession] = useState(null);    // saved pick-run progress, if any
  const [includeCustom, setIncludeCustom] = useState(false); // fold custom pick into the full order run
  const [customInRun, setCustomInRun] = useState(false);     // custom items are in the current pick run
  const [customDeductResolved, setCustomDeductResolved] = useState(false);
  const customInvRef = useRef(null);                        // part/color -> {colorId,bin,qty}
  const [excluded, setExcluded] = useState(new Set());
  const [syncMode, setSyncModeState] = useState('off');
  const [syncResults, setSyncResults] = useState(null);

  useEffect(() => {
    function handleKey(e) {
      if (e.code !== 'Space') return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (screen !== 'picking') return;
      e.preventDefault();
      const binItems = pickQueue[pickIndex]?.items || [];
      const unpicked = binItems.find(item => !picked.has(item.pickKey));
      if (unpicked) {
        setPicked(prev => new Set([...prev, unpicked.pickKey]));
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [screen, pickIndex, pickQueue, picked]);
  // Auto-advance to next bin when current bin is fully picked
  useEffect(() => {
    if (screen !== 'picking') return;
    const currentBin = pickQueue[pickIndex];
    if (!currentBin || currentBin.items.length === 0) return;
    const allDone = currentBin.items.every(i => picked.has(i.pickKey));
    if (!allDone) return;
    const timer = setTimeout(() => {
      setPickIndex(prev => prev + 1);
    }, 800);
    return () => clearTimeout(timer);
  }, [screen, pickIndex, pickQueue, picked]);

  // Load the set of orders the user has excluded from pick runs.
  useEffect(() => {
    async function loadExcluded() {
      const { data } = await supabase.from('excluded_orders').select('order_id');
      if (data) setExcluded(new Set(data.map(r => r.order_id)));
    }
    loadExcluded();
    getSyncMode().then(setSyncModeState);
    async function loadReviewed() {
      const { data } = await supabase.from('reconcile_reviewed').select('bl_lot_id');
      if (data) setReviewedLots(new Set(data.map(r => String(r.bl_lot_id))));
    }
    loadReviewed();
    async function loadAuditDates() {
      const { data } = await supabase.from('bin_audit_dates').select('bin_name, last_audited');
      if (data) {
        const m = {};
        data.forEach(r => { if (r.bin_name) m[r.bin_name] = r.last_audited; });
        setBinAuditDates(m);
      }
    }
    loadAuditDates();
    loadCustomItems();
    loadSavedSession();
  }, []);

  async function loadCustomItems() {
    const { data } = await supabase.from('custom_pick_items').select('*').order('created_at');
    if (data) setCustomItems(data);
  }

  // Build a lookup of the user's BrickLink inventory so a manually-entered
  // part+color can be matched to a real lot (for its bin, color id, and stock).
  async function buildCustomInvLookup() {
    if (customInvRef.current) return customInvRef.current;
    const byPartColor = {}; // "partlower|colorlower" -> {colorId,bin,qty}
    const byPart = {};      // "partlower" -> [{color,colorId,bin,qty}]
    try {
      const inv = await fetch(`${API}/api/inventories`).then(r => r.json());
      (inv.data || []).forEach(lot => {
        const part = (lot.item?.no || '').toLowerCase();
        const color = (lot.color_name || '');
        const bin = getPrimaryBin(lot.remarks);
        const rec = { color, colorId: lot.color_id || 0, bin, qty: lot.quantity || 0 };
        const pcKey = `${part}|${color.toLowerCase()}`;
        if (!byPartColor[pcKey]) byPartColor[pcKey] = { colorId: rec.colorId, bin, qty: 0 };
        byPartColor[pcKey].qty += rec.qty;
        if (!byPartColor[pcKey].bin && bin) byPartColor[pcKey].bin = bin;
        if (!byPart[part]) byPart[part] = [];
        byPart[part].push(rec);
      });
    } catch (e) { console.error('custom inv lookup error:', e); }
    customInvRef.current = { byPartColor, byPart };
    return customInvRef.current;
  }

  // Add one line to the custom pick, resolving bin/color from inventory.
  async function addCustomLine(partRaw, colorRaw, qtyRaw) {
    const part = (partRaw || '').trim();
    const qty = parseInt(qtyRaw, 10) || 0;
    if (!part || qty <= 0) return false;
    const color = (colorRaw || '').trim();
    const { byPartColor, byPart } = await buildCustomInvLookup();
    const partKey = part.toLowerCase();
    let colorId = 0, bin = '(check BrickLink)', matched = false, colorName = color;

    let hit = color ? byPartColor[`${partKey}|${color.toLowerCase()}`] : null;
    if (!hit && !color && byPart[partKey] && byPart[partKey].length === 1) {
      // No color given but only one color in stock — use it.
      const only = byPart[partKey][0];
      hit = { colorId: only.colorId, bin: only.bin, qty: only.qty };
      colorName = only.color;
    }
    if (hit) { colorId = hit.colorId; bin = hit.bin || '(no bin)'; matched = true; }

    const { error } = await supabase.from('custom_pick_items').insert({
      part_no: part, color: colorName, color_id: colorId, qty, bin, matched,
    });
    if (error) { console.error('add custom line error:', error); return false; }
    await loadCustomItems();
    return true;
  }

  // Parse a pasted block: one part per line, e.g. "3001 Red 4" or "3001, Red, 4".
  async function addCustomPaste(text) {
    const lines = (text || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    setCustomBusy(true);
    for (const line of lines) {
      let part = '', color = '', qty = '1';
      if (line.includes(',')) {
        const p = line.split(',').map(s => s.trim());
        part = p[0]; color = p[1] || ''; qty = p[2] || '1';
      } else {
        const toks = line.split(/\s+/);
        part = toks[0] || '';
        if (toks.length >= 2 && /^\d+$/.test(toks[toks.length - 1])) {
          qty = toks[toks.length - 1];
          color = toks.slice(1, -1).join(' ');
        } else {
          color = toks.slice(1).join(' ');
        }
      }
      await addCustomLine(part, color, qty);
    }
    setCustomBusy(false);
  }

  async function removeCustomItem(id) {
    await supabase.from('custom_pick_items').delete().eq('id', id);
    await loadCustomItems();
  }

  async function clearCustomAll() {
    await supabase.from('custom_pick_items').delete().neq('id', '00000000-0000-0000-0000-000000000000');
    setCustomItems([]);
  }

  // Run the custom list through the picker as a standalone pseudo-order.
  async function startCustomPick() {
    if (customItems.length === 0) return;
    setLoadingPickRun(true);
    const invMap = {};
    try {
      const inv = await fetch(`${API}/api/inventories`).then(r => r.json());
      (inv.data || []).forEach(lot => {
        const key = `${lot.item?.no || ''}-${lot.color_id || 0}`;
        if (!invMap[key]) invMap[key] = { qty: 0, bin: '' };
        invMap[key].qty += (lot.quantity || 0);
        const b = getPrimaryBin(lot.remarks);
        if (!invMap[key].bin && b) invMap[key].bin = b;
      });
    } catch (e) { /* non-fatal */ }

    const demand = {};
    customItems.forEach(it => { const k = `${it.part_no}-${it.color_id}`; demand[k] = (demand[k] || 0) + (it.qty || 0); });

    const pickItems = customItems.map((it, j) => {
      const k = `${it.part_no}-${it.color_id}`;
      const stock = invMap[k];
      const noBin = !it.bin || it.bin === '(check BrickLink)' || it.bin === '(no bin)';
      const bin = (noBin && stock && stock.bin) ? stock.bin : (it.bin || '(no bin)');
      const remaining = stock ? Math.max(0, stock.qty - (demand[k] || 0)) : null;
      return {
        partNo: it.part_no, name: it.part_no, type: 'P', color: it.color, colorId: it.color_id,
        qty: it.qty, bin, remaining,
        orderId: 'custom', orderBuyer: 'Custom Pick', orderColor: '#7c3aed', orderLetter: 'C',
        pickKey: `custom-${it.id}`,
      };
    });
    pickItems.sort((a, b) => {
      if (a.bin === '(no bin)') return 1;
      if (b.bin === '(no bin)') return -1;
      return a.bin.localeCompare(b.bin);
    });
    const binMap = {};
    pickItems.forEach(i => { if (!binMap[i.bin]) binMap[i.bin] = []; binMap[i.bin].push(i); });
    setPickQueue(Object.entries(binMap).map(([bin, items]) => ({ bin, items })));
    setPickIndex(0);
    setPicked(new Set());
    setCustomMode(true);
    setScreen('picking');
    setLoadingPickRun(false);
  }

  // Deduct the custom-pick quantities from BrickLink (best-effort, floored).
  async function deductCustomFromBL() {
    try {
      const inv = await fetch(`${API}/api/inventories?fresh=1`).then(r => r.json());
      const lots = inv.data || [];
      for (const it of customItems) {
        const lot = lots.find(l => (l.item?.no || '') === it.part_no && (l.color_id || 0) === it.color_id && (l.quantity || 0) > 0);
        if (!lot) continue;
        const ded = Math.min(it.qty, lot.quantity);
        if (ded > 0) {
          await fetch(`${API}/api/inventories/${lot.inventory_id}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ quantity: -ded }),
          });
        }
      }
    } catch (e) { console.error('custom deduct error:', e); }
  }

  // Finish a STANDALONE custom pick: optionally deduct, then clear + go home.
  async function finishCustomPick(deduct) {
    if (deduct) await deductCustomFromBL();
    await clearCustomAll();
    await clearSavedSession();
    customInvRef.current = null;
    setCustomMode(false);
    setPickQueue([]);
    setPicked(new Set());
    setScreen('list');
  }

  // ── Save / resume pick progress ──────────────────────────────────────────
  async function loadSavedSession() {
    try {
      const { data } = await supabase.from('pick_sessions').select('saved_at').eq('id', 'current').maybeSingle();
      setSavedSession(data || null);
    } catch (e) { /* ignore */ }
  }

  async function saveProgress() {
    const snapshot = {
      orders, orderItems,
      excluded: [...excluded],
      pickQueue,
      picked: [...picked],
      pickIndex,
      customMode, customInRun,
    };
    try {
      await supabase.from('pick_sessions').upsert(
        { id: 'current', snapshot, saved_at: new Date().toISOString() },
        { onConflict: 'id' }
      );
      setSavedSession({ saved_at: new Date().toISOString() });
      setScreen('list');
    } catch (e) { console.error('save progress error:', e); }
  }

  async function resumeSession() {
    try {
      const { data } = await supabase.from('pick_sessions').select('snapshot').eq('id', 'current').maybeSingle();
      const s = data?.snapshot;
      if (!s) { setSavedSession(null); return; }
      setOrders(s.orders || []);
      setOrderItems(s.orderItems || {});
      setExcluded(new Set(s.excluded || []));
      setPickQueue(s.pickQueue || []);
      setPicked(new Set(s.picked || []));
      setPickIndex(s.pickIndex || 0);
      setCustomMode(!!s.customMode);
      setCustomInRun(!!s.customInRun);
      setScreen('picking');
    } catch (e) { console.error('resume error:', e); }
  }

  async function clearSavedSession() {
    try { await supabase.from('pick_sessions').delete().eq('id', 'current'); } catch (e) { /* ignore */ }
    setSavedSession(null);
  }

  // Resolve the custom portion of a COMBINED run (after real orders shipped).
  async function resolveCombinedCustom(deduct) {
    if (deduct) await deductCustomFromBL();
    await clearCustomAll();
    customInvRef.current = null;
    setCustomDeductResolved(true);
  }

  // Add custom-pick lines from a dropped CSV. Header-aware (Part/Color/Qty
  // columns) with a positional fallback ("3001 Red 4"), reusing the same
  // matching as manual entry.
  function handleCustomCsv(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const text = String(reader.result || '');
      if (text.slice(0, 2) === 'PK') {
        alert('That looks like an Excel (.xlsx) file. In Excel choose File → Save As → CSV, then drop that.');
        return;
      }
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      if (lines.length === 0) return;
      const first = splitCsvLine(lines[0]).map(h => h.toLowerCase());
      const looksHeader = first.some(h => h.includes('part') || h.includes('design') || h.includes('item') || h.includes('color') || h.includes('qty') || h.includes('quantity'));
      let iPart = -1, iColor = -1, iQty = -1, start = 0;
      if (looksHeader) {
        iPart = first.findIndex(h => h.includes('part') || h.includes('design') || h.includes('item'));
        iColor = first.findIndex(h => h.includes('color') || h.includes('colour'));
        iQty = first.findIndex(h => h.includes('qty') || h.includes('quantity') || h.includes('count'));
        start = 1;
      }
      setCustomBusy(true);
      for (let r = start; r < lines.length; r++) {
        const cells = splitCsvLine(lines[r]);
        if (looksHeader) {
          await addCustomLine(cells[iPart] || '', iColor >= 0 ? (cells[iColor] || '') : '', iQty >= 0 ? (cells[iQty] || '1') : '1');
        } else {
          // No header: treat like a pasted line.
          await addCustomPaste(lines[r]);
        }
      }
      setCustomBusy(false);
    };
    reader.readAsText(file);
  }

  // When the ship screen opens, resolve each buyer's real name for the
  // messages (once per order).
  useEffect(() => {
    if (screen !== 'fulfill') return;
    const toResolve = orders.filter(o => !excluded.has(o.id) && !realNames[o.id]);
    if (toResolve.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        toResolve.map(async o => [o.id, await resolveBuyerInfo(o)])
      );
      if (cancelled) return;
      setRealNames(prev => {
        const next = { ...prev };
        entries.forEach(([id, info]) => { next[id] = info.name; });
        return next;
      });
      setBuyerEmails(prev => {
        const next = { ...prev };
        entries.forEach(([id, info]) => { if (info.email) next[id] = info.email; });
        return next;
      });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, orders, excluded]);

  // A reconcile lot is auto-cleared when its bin was physically audited AFTER
  // the most recent BrickOwl sale that raised the concern — the audit is the
  // ground-truth count, so the phantom flag is resolved. Returns the audit
  // date (YYYY-MM-DD) if cleared, else null.
  function auditClearedDate(f) {
    const bin = getPrimaryBin(f.remarks);
    if (!bin) return null;
    const audited = binAuditDates[bin];
    if (!audited) return null;
    const auditedDay = String(audited).slice(0, 10);
    const latestSale = (f.orders || []).reduce((m, o) => (o.date && o.date > m ? o.date : m), '');
    if (!latestSale) return null;
    return auditedDay >= latestSale ? auditedDay : null;
  }

  // Hide a reconcile lot as "reviewed" (persisted; changes no stock).
  async function markReviewed(f) {
    const key = String(f.blLotId);
    setReviewedLots(prev => new Set(prev).add(key));
    try {
      await supabase.from('reconcile_reviewed').upsert(
        { bl_lot_id: key, part_no: f.partNo, color: f.color },
        { onConflict: 'bl_lot_id' }
      );
    } catch (e) { console.error('mark reviewed error:', e); }
  }

  async function unhideAllReviewed() {
    setReviewedLots(new Set());
    try { await supabase.from('reconcile_reviewed').delete().neq('bl_lot_id', ''); }
    catch (e) { console.error('unhide all error:', e); }
  }

  // Shared renderer for both reconcile directions. cfg supplies which side sold
  // and which side still lists, plus the field names on each finding.
  function renderReconcilePanel(data, cfg) {
    if (!data) return null;
    if (data.error) return <div style={{ color: '#dc2626', fontSize: '0.82rem' }}>Check failed: {data.error}</div>;
    const all = data.findings || [];
    if (all.length === 0) {
      return <div style={{ fontSize: '0.85rem', color: colors.greenDark, fontWeight: 700 }}>✓ No mismatches — every recent {cfg.soldWho} sale is reflected on {cfg.listWho}.</div>;
    }
    const auditCleared = all.filter(f => auditClearedDate(f));
    const visible = all.filter(f => !reviewedLots.has(String(f.blLotId)) && !auditClearedDate(f));
    const manualHidden = all.filter(f => reviewedLots.has(String(f.blLotId)) && !auditClearedDate(f)).length;
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 800, color: colors.navy, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {visible.length} lot{visible.length === 1 ? '' : 's'} to review · scanned {data.ordersScanned} order{data.ordersScanned === 1 ? '' : 's'}
          </div>
          {manualHidden > 0 && (
            <button onClick={unhideAllReviewed} style={{ background: 'none', border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '3px 8px', fontFamily: "'Outfit', sans-serif", fontSize: '0.64rem', fontWeight: 700, color: colors.textSecondary, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {manualHidden} hidden · unhide
            </button>
          )}
        </div>
        {auditCleared.length > 0 && (
          <div style={{ fontSize: '0.7rem', color: colors.greenDark, fontWeight: 700, marginBottom: '8px' }}>
            ✓ {auditCleared.length} auto-cleared by a bin audit since the sale
          </div>
        )}
        {visible.length === 0 ? (
          <div style={{ fontSize: '0.82rem', color: colors.greenDark, fontWeight: 700 }}>✓ All reviewed — nothing left in this list.</div>
        ) : visible.map((f, i) => (
          <div key={f.blLotId || i} style={{ padding: '8px 0', borderTop: i ? `1px solid ${colors.border}` : 'none', fontSize: '0.8rem', color: colors.textPrimary, lineHeight: 1.4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontWeight: 700 }}>{f.partNo}</span> · {f.color}
              {f.risk === 'high' && <span style={{ marginLeft: '6px', background: '#fef2f2', color: '#dc2626', fontWeight: 800, fontSize: '0.62rem', padding: '2px 6px', borderRadius: '4px', textTransform: 'uppercase' }}>likely phantom</span>}
              <div style={{ color: colors.textSecondary, fontSize: '0.75rem', marginTop: '2px' }}>
                {cfg.soldWho} sold {f[cfg.soldKey]} recently, {cfg.listWho} still lists <strong>{f[cfg.listKey]}</strong> · {getPrimaryBin(f.remarks) || '(no bin)'}
              </div>
            </div>
            <button onClick={() => markReviewed(f)} title="Mark reviewed — hides it, changes no stock" style={{ flexShrink: 0, background: colors.cardBg, border: `1px solid ${colors.greenDark}`, color: colors.greenDark, borderRadius: '6px', padding: '5px 10px', fontFamily: "'Outfit', sans-serif", fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer' }}>✓ Reviewed</button>
          </div>
        ))}
        <div style={{ fontSize: '0.68rem', color: colors.textSecondary, marginTop: '8px' }}>
          Read-only — "✓ Reviewed" only hides a lot (changes no stock). Auditing a bin auto-clears its flagged lots (the count is verified). Fix real quantities on {cfg.fixWho}. "Likely phantom" = {cfg.soldWho} sold as many or more than {cfg.listWho} still shows; deep-stock/restocked parts can show here without being a true problem.
        </div>
      </>
    );
  }

  async function changeSyncMode(mode) {
    setSyncModeState(mode);
    try { await setSyncMode(mode); } catch (e) { console.error('sync mode save error:', e); }
  }

  async function loadOrders() {
    setLoading(true);
    blLotMapRef.current = null; // refresh inventory lot map on each reload
    try {
      const res = await fetch(`${API}/api/orders`);
      const json = await res.json();
      const raw = json.data || [];
      const EXCLUDE = ['SHIPPED', 'RECEIVED', 'COMPLETED', 'CANCELLED', 'PURGED'];
      const mapped = raw
        .filter(o => !EXCLUDE.includes((o.status || '').toUpperCase()))
        .map(o => ({
          id: o.order_id,
          buyer: o.buyer_name || 'Unknown',
          status: statusToStage(o.status),
          blStatus: o.status,
          date: o.date_ordered?.split('T')[0] || '',
          total: parseFloat(o.cost?.grand_total || 0).toFixed(2),
          itemCount: o.total_count || 0,
          source: 'bricklink',
        }));

      // Fetch BrickOwl orders
      let boMapped = [];
      try {
        const boRes = await fetch(`${API}/api/brickowl/orders`);
        const boJson = await boRes.json();
        const boRaw = Array.isArray(boJson) ? boJson : (boJson.orders || []);
        const BO_KEEP = ['payment received', 'processing', 'paid'];
        boMapped = boRaw
          .filter(o => BO_KEEP.includes((o.status || '').toLowerCase()))
          .map(o => ({
            id: `BO-${o.order_id}`,
            boId: o.order_id,
            buyer: o.customer_username || o.ship_first_name || 'BrickOwl Buyer',
            status: 'Pending',
            blStatus: o.status,
            date: o.ordered_at?.split('T')[0] || '',
            total: parseFloat(o.base_order_total || 0).toFixed(2),
            itemCount: parseInt(o.total_quantity || 0),
            source: 'brickowl',
          }));
      } catch (boErr) {
        console.error('BrickOwl error:', boErr);
      }

      const sorted = [...mapped, ...boMapped]
        .sort((a, b) => new Date(b.date) - new Date(a.date)); // newest on top for display

      // Stable letters: an order keeps its letter for life; a new order takes
      // the next free letter (no reshuffle). Letters free up when an order
      // ships (drops out of the open set).
      const letterMap = {};
      try {
        const { data } = await supabase.from('order_letters').select('order_id, letter');
        (data || []).forEach(r => { letterMap[String(r.order_id)] = r.letter; });
      } catch (e) { /* fall back to positional below */ }

      const presentIds = new Set(sorted.map(o => String(o.id)));
      const taken = new Set();
      sorted.forEach(o => { const L = letterMap[String(o.id)]; if (L) taken.add(L); });
      const nextFree = () => {
        for (let c = 65; c < 65 + 26; c++) { const L = String.fromCharCode(c); if (!taken.has(L)) return L; }
        return '?';
      };
      // Assign new orders oldest-first so recent arrivals land on later letters.
      const newAssign = [];
      sorted.filter(o => !letterMap[String(o.id)])
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .forEach(o => { const L = nextFree(); taken.add(L); letterMap[String(o.id)] = L; newAssign.push({ order_id: String(o.id), letter: L }); });
      if (newAssign.length) {
        try { await supabase.from('order_letters').upsert(newAssign, { onConflict: 'order_id' }); } catch (e) { /* non-fatal */ }
      }
      // Free letters held by orders that are no longer open.
      try {
        const gone = Object.keys(letterMap).filter(id => !presentIds.has(id));
        if (gone.length) await supabase.from('order_letters').delete().in('order_id', gone);
      } catch (e) { /* non-fatal */ }

      const all = sorted.map((o, i) => {
        const L = letterMap[String(o.id)] || String.fromCharCode(65 + i);
        const idx = L.charCodeAt(0) - 65;
        return { ...o, letter: L, color: ORDER_COLORS[((idx % ORDER_COLORS.length) + ORDER_COLORS.length) % ORDER_COLORS.length] };
      });
      setOrders(all);
    } catch (err) {
      console.error('Error loading orders:', err);
    }
    setLoading(false);
  }

  // Oversell alarm (read-only — never edits inventory). BrickLink already
  // reserves its own open orders out of the available count, so any open

  // Stage 1 reconciliation (read-only): ask the server to find BrickOwl sales
  // that BrickLink never deducted (phantom stock). On demand, not on load.
  async function runReconcile() {
    setReconciling(true);
    try {
      const res = await fetch(`${API}/api/reconcile/brickowl?days=120`);
      const json = await res.json();
      setReconcile(json);
    } catch (err) {
      setReconcile({ error: err.message, findings: [] });
    }
    setReconciling(false);
  }

  async function runReconcileRev() {
    setReconcilingRev(true);
    try {
      const res = await fetch(`${API}/api/reconcile/bricklink?days=120`);
      const json = await res.json();
      setReconcileRev(json);
    } catch (err) {
      setReconcileRev({ error: err.message, findings: [] });
    }
    setReconcilingRev(false);
  }

  // Build (once per load) a map of BrickLink inventory lots keyed by lot id
  // (inventory_id). BrickOwl order lines carry the matching bl_lot_id, which
  // lets us pull the true BrickLink number / color / bin from our own inventory.
  async function getBlLotMap() {
    if (blLotMapRef.current) return blLotMapRef.current;
    const map = {};
    try {
      const res = await fetch(`${API}/api/inventories`);
      const json = await res.json();
      (json.data || []).forEach(lot => {
        if (lot.inventory_id == null) return;
        map[String(lot.inventory_id)] = {
          partNo: lot.item?.no || '',
          colorId: lot.color_id || 0,
          colorName: lot.color_name || '',
          bin: getPrimaryBin(lot.remarks),
        };
      });
    } catch (err) { console.error('BL lot map error:', err); }
    blLotMapRef.current = map;
    return map;
  }

  async function fetchItemsForOrder(orderId, order) {
    let items;
    if (order?.source === 'brickowl') {
      const res = await fetch(`${API}/api/brickowl/orders/${order.boId}/items`);
      const json = await res.json();
      const raw = Array.isArray(json) ? json : (json.items || []);
      const blLotMap = await getBlLotMap();
      items = raw.map(item => {
        const idTypes = item.ids || [];
        const getId = (type) => idTypes.find(i => i.type === type)?.id;
        // Match this BrickOwl line to our BrickLink inventory lot via bl_lot_id.
        // When matched, use BrickLink's own item number, color name, color id,
        // and bin (the source of truth). Fall back to BrickOwl's data otherwise.
        const lot = item.bl_lot_id != null ? blLotMap[String(item.bl_lot_id)] : null;
        const partNo = (lot && lot.partNo) || getId('bl_item_no') || getId('design_id') || getId('part_number') || getId('ldraw_id') || item.boid || '';
        const colorId = lot ? lot.colorId : null;
        const color = (lot && lot.colorName) || item.color_name || '';
        const bin = (lot && lot.bin) || getPrimaryBin(item.personal_note) || '(check BrickLink)';
        return {
          partNo,
          name: item.name || 'Unknown',
          type: 'P',
          color,
          colorId,
          qty: parseInt(item.ordered_quantity || 1),
          bin,
          hasOverflow: !!(item.personal_note && /\s\+/.test(item.personal_note)),
          boImage: item.image_small || '',
          blMatched: !!lot,
        };
      });
    } else {
      const res = await fetch(`${API}/api/orders/${orderId}/items`);
      const json = await res.json();
      const batches = json.data || [];
      items = batches.flat().map(item => ({
        partNo: item.item?.no || '',
        name: item.item?.name || 'Unknown',
        type: item.item?.type || item.item?.type_name || 'P',
        color: item.color_name || '',
        colorId: item.color_id || 0,
        qty: item.quantity || 0,
        bin: getPrimaryBin(item.remarks) || '(no bin)',
        hasOverflow: !!(item.remarks && /\s\+/.test(item.remarks)),
      }));
    }
    // Sort by bin (walk path) — items with no bin go last
    items.sort((a, b) => {
      const aNoBin = a.bin === '(no bin)' || a.bin === '(check BrickLink)';
      const bNoBin = b.bin === '(no bin)' || b.bin === '(check BrickLink)';
      if (aNoBin && !bNoBin) return 1;
      if (!aNoBin && bNoBin) return -1;
      return a.bin.localeCompare(b.bin);
    });
    return items;
  }

  async function toggleExpand(orderId) {
    if (expandedOrder === orderId) { setExpandedOrder(null); return; }
    setExpandedOrder(orderId);
    if (!orderItems[orderId]) {
      setLoadingItems(prev => ({ ...prev, [orderId]: true }));
      try {
        const order = orders.find(o => o.id === orderId);
        const items = await fetchItemsForOrder(orderId, order);
        setOrderItems(prev => ({ ...prev, [orderId]: items }));
      } catch (err) { console.error(err); }
      setLoadingItems(prev => ({ ...prev, [orderId]: false }));
    }
  }

  async function startPickRun() {
    setLoadingPickRun(true);
    const allPickItems = [];
    const newOrderItems = { ...orderItems };

    // Fetch inventory once to calculate "remaining after pick"
    const invMap = {};
    try {
      const invRes = await fetch(`${API}/api/inventories`);
      const invJson = await invRes.json();
      const invItems = invJson.data || [];
      invItems.forEach(inv => {
        const partNo = inv.item?.no || '';
        const colorId = inv.color_id || 0;
        const key = `${partNo}-${colorId}`;
        const bin = getPrimaryBin(inv.remarks);
        if (!invMap[key]) invMap[key] = { qty: 0, bin: '' };
        invMap[key].qty += (inv.quantity || 0);
        if (!invMap[key].bin && bin) invMap[key].bin = bin;
      });
    } catch (err) { console.error('Inventory fetch error:', err); }

    // First pass: make sure every order's items are loaded, and tally how many
    // of each part+color this whole pick run will consume across ALL orders.
    // Without this, a part needed by two orders would subtract only one order's
    // quantity from the full stock total and show too many "left" on each card.
    const demandMap = {};
    for (const order of orders) {
      if (excluded.has(order.id)) continue; // skip orders excluded from this run
      let items = newOrderItems[order.id];
      if (!items) {
        try { items = await fetchItemsForOrder(order.id, order); }
        catch (err) { items = []; }
        newOrderItems[order.id] = items;
      }
      items.forEach(item => {
        const invKey = `${item.partNo}-${item.colorId}`;
        demandMap[invKey] = (demandMap[invKey] || 0) + (item.qty || 0);
      });
    }
    // Fold the custom pick into the same demand tally if it's included.
    if (includeCustom) {
      customItems.forEach(it => {
        const invKey = `${it.part_no}-${it.color_id}`;
        demandMap[invKey] = (demandMap[invKey] || 0) + (it.qty || 0);
      });
    }

    // Second pass: build the pick list. "N left" = total stock minus the full
    // run's demand for that part+color (the count you'll have after shipping
    // everything in this session).
    for (const order of orders) {
      if (excluded.has(order.id)) continue; // skip orders excluded from this run
      const items = newOrderItems[order.id] || [];
      items.forEach((item, j) => {
        const invKey = `${item.partNo}-${item.colorId}`;
        const stock = invMap[invKey];
        const totalInStock = stock ? stock.qty : undefined;
        const remaining = totalInStock !== undefined ? Math.max(0, totalInStock - (demandMap[invKey] || 0)) : null;
        // BrickOwl lines (and any line missing a bin) inherit the bin from the
        // matched BrickLink lot so they join the bin-sorted walk path.
        const noBin = item.bin === '(no bin)' || item.bin === '(check BrickLink)';
        const bin = (noBin && stock && stock.bin) ? stock.bin : item.bin;
        allPickItems.push({
          ...item,
          bin,
          remaining,
          orderId: order.id,
          orderBuyer: order.buyer,
          orderColor: order.color,
          orderLetter: order.letter,
          pickKey: `${order.id}-${item.partNo}-${item.colorId}-${j}`,
        });
      });
    }

    // Append the custom pick as a purple "C" pseudo-order in the same walk.
    if (includeCustom) {
      customItems.forEach(it => {
        const invKey = `${it.part_no}-${it.color_id}`;
        const stock = invMap[invKey];
        const remaining = stock ? Math.max(0, stock.qty - (demandMap[invKey] || 0)) : null;
        const noBin = !it.bin || it.bin === '(check BrickLink)' || it.bin === '(no bin)';
        const bin = (noBin && stock && stock.bin) ? stock.bin : (it.bin || '(no bin)');
        allPickItems.push({
          partNo: it.part_no, name: it.part_no, type: 'P', color: it.color, colorId: it.color_id,
          qty: it.qty, bin, remaining,
          orderId: 'custom', orderBuyer: 'Custom Pick', orderColor: '#7c3aed', orderLetter: 'C',
          pickKey: `custom-${it.id}`,
        });
      });
    }
    setCustomInRun(includeCustom && customItems.length > 0);
    setCustomDeductResolved(false);

    setOrderItems(newOrderItems);
    allPickItems.sort((a, b) => {
      if (a.bin === '(no bin)') return 1;
      if (b.bin === '(no bin)') return -1;
      return a.bin.localeCompare(b.bin);
    });

    const binMap = {};
    allPickItems.forEach(item => {
      if (!binMap[item.bin]) binMap[item.bin] = [];
      binMap[item.bin].push(item);
    });

    const queue = Object.entries(binMap).map(([bin, items]) => ({ bin, items }));
    setPickQueue(queue);
    setPickIndex(0);
    setPicked(new Set());
    setScreen('picking');
    setLoadingPickRun(false);
  }

  // Resolve a buyer's real ship-to name AND email from order detail, so
  // shipping messages read "Hi David" instead of "Hi keep1010" and so a
  // tracking export can be matched by name/email. Defensive across the two
  // APIs; always falls back to the username.
  async function resolveBuyerInfo(order) {
    try {
      if (order.source === 'brickowl') {
        const d = await fetch(`${API}/api/brickowl/orders/${order.boId}`).then(r => r.json());
        const o = Array.isArray(d) ? d[0] : (d.order || d);
        const name = o?.customer_name || o?.name
          || [o?.ship_first_name, o?.ship_last_name].filter(Boolean).join(' ')
          || [o?.first_name, o?.last_name].filter(Boolean).join(' ')
          || o?.shipping_address?.name || o?.shipping?.name;
        const email = o?.customer_email || o?.email || o?.buyer_email || '';
        return { name: (name || '').trim() || order.buyer, email: (email || '').trim().toLowerCase() };
      }
      const d = await fetch(`${API}/api/orders/${order.id}`).then(r => r.json());
      const o = d?.data || d;
      const nm = o?.shipping?.address?.name;
      const name = (nm && (nm.full || [nm.first, nm.last].filter(Boolean).join(' '))) || o?.buyer_name;
      const email = o?.buyer_email || o?.email || '';
      return { name: (name || '').trim() || order.buyer, email: (email || '').trim().toLowerCase() };
    } catch (e) {
      return { name: order.buyer, email: '' };
    }
  }

  // Split one CSV line, honoring double-quoted fields that contain commas.
  function splitCsvLine(line) {
    const out = []; let cur = ''; let q = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (q) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') q = false;
        else cur += ch;
      } else if (ch === '"') q = true;
      else if (ch === ',') { out.push(cur); cur = ''; }
      else cur += ch;
    }
    out.push(cur);
    return out.map(s => s.trim());
  }

  // Import tracking from a shipping-export CSV (e.g. PayPal's). Header-aware:
  // finds the tracking / order-id / email / recipient columns by name, then
  // matches each row to an order by order id, then email, then ship-to name.
  function handleTrackingCsv(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      if (text.slice(0, 2) === 'PK') {
        setTrackingImport({ error: 'That looks like an Excel (.xlsx) file. In Excel choose File → Save As → CSV, then drop that file.' });
        return;
      }
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { setTrackingImport({ error: 'That file has no data rows.' }); return; }
      const header = splitCsvLine(lines[0]).map(h => h.toLowerCase());
      const colFor = (...keys) => header.findIndex(h => keys.some(k => h.includes(k)));
      const iTrack = colFor('tracking number', 'tracking no', 'tracking');
      const iOrder = colFor('order id', 'order number', 'order');
      const iEmail = colFor('email');
      const iName = colFor('recipient', 'ship to', 'name');
      if (iTrack === -1) { setTrackingImport({ error: 'Could not find a "Tracking Number" column in that file.' }); return; }

      const active = orders.filter(o => !excluded.has(o.id));
      const byId = new Map(), byEmail = new Map(), byName = new Map();
      active.forEach(o => {
        byId.set(String(o.id), o); if (o.boId) byId.set(String(o.boId), o);
        const em = (buyerEmails[o.id] || '').toLowerCase(); if (em) byEmail.set(em, o);
        const nm = (realNames[o.id] || o.buyer || '').trim().toLowerCase(); if (nm) byName.set(nm, o);
      });

      const found = {};
      for (let r = 1; r < lines.length; r++) {
        const cells = splitCsvLine(lines[r]);
        const track = (cells[iTrack] || '').trim();
        if (!track) continue;
        let match = null;
        if (iOrder !== -1 && cells[iOrder]) match = byId.get(cells[iOrder].trim());
        if (!match && iEmail !== -1 && cells[iEmail]) match = byEmail.get(cells[iEmail].trim().toLowerCase());
        if (!match && iName !== -1 && cells[iName]) match = byName.get(cells[iName].trim().toLowerCase());
        if (match) found[match.id] = track;
      }

      setTracking(prev => ({ ...prev, ...found }));
      const unmatched = active.filter(o => !found[o.id]).map(o => o.letter);
      setTrackingImport({ matched: Object.keys(found).length, total: active.length, unmatched });
    };
    reader.readAsText(file);
  }

  async function fulfillOrders() {
    setFulfilling(true);
    const results = { success: [], errors: [] };
    const syncReport = [];

    for (const order of orders) {
      if (excluded.has(order.id)) continue; // excluded orders aren't shipped/messaged/logged
      const items = orderItems[order.id] || [];
      const totalPieces = items.reduce((sum, i) => sum + i.qty, 0);
      const trackingNo = (tracking[order.id] || '').trim();
      const buyerName = realNames[order.id] || order.buyer;
      const resolvedMsg = resolveTemplate(DEFAULT_MESSAGE, {
        buyerName, orderId: order.id,
        pieces: totalPieces, total: order.total,
      });
      const resolvedFb = resolveTemplate(DEFAULT_FEEDBACK, {
        buyerName, orderId: order.id,
        pieces: totalPieces, total: order.total,
      });

      try {
        if (order.source === 'brickowl') {
          // BrickOwl: set status to shipped (status_id 5 = Shipped)
          await fetch(`${API}/api/brickowl/orders/${order.boId}/status`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status_id: 5 }),
          });

          // BrickOwl: add tracking
          if (trackingNo) {
            await fetch(`${API}/api/brickowl/orders/${order.boId}/tracking`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ tracking_number: trackingNo }),
            });
          }

          // BrickOwl: leave feedback
          await fetch(`${API}/api/brickowl/feedback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ order_id: order.boId, feedback: resolvedFb }),
          });

        } else {
          // BrickLink: set order status to Shipped
          await fetch(`${API}/api/orders/${order.id}/status`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ field: 'status', value: 'Shipped' }),
          });

          // BrickLink: set payment status to Completed
          await fetch(`${API}/api/orders/${order.id}/status`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ field: 'payment_status', value: 'Completed' }),
          });

          // BrickLink: tracking
          if (trackingNo) {
            const dateShipped = new Date().toISOString().replace(/\.\d{3}Z$/, '.000Z');
            await fetch(`${API}/api/orders/${order.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ shipping: { tracking_no: trackingNo, date_shipped: dateShipped } }),
            });
          }

          // BrickLink: drive-thru message
          await fetch(`${API}/api/orders/${order.id}/drive_thru`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ body: resolvedMsg, subject: 'Order Shipped' }),
          });

          // BrickLink: feedback
          await fetch(`${API}/api/feedback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              order_id: parseInt(order.id),
              rating: 0,
              rating_of_bs: 'S',
              comment: resolvedFb,
            }),
          });
        }

        results.success.push(order.id);

        // Auto-log income to Finances
        try {
          await supabase.from('expenses').insert({
            date: new Date().toISOString().split('T')[0],
            vendor: order.buyer,
            description: `Order #${order.id}`,
            amount: parseFloat(order.total) || 0,
            category: order.source === 'brickowl' ? 'BrickOwl Sales' : 'BrickLink Sales',
            type: 'income',
          });
        } catch (e) { console.error('Income log error:', e); }

        // Cross-platform inventory sync: reduce the OTHER store so this sale
        // can't be sold again there. Never blocks shipping — errors are
        // reported, not thrown.
        try {
          const rep = await syncDeductForOrder(order);
          if (rep && rep.lines) {
            syncReport.push({ orderId: order.id, source: order.source, ...rep });
          }
        } catch (e) { console.error('Sync deduct error:', e); }

      } catch (err) {
        results.errors.push({ orderId: order.id, error: err.message });
      }
    }

    setFulfillResults(results);
    setSyncResults(syncReport);
    setFulfilling(false);
    if (results.success.length > 0) {
      setOrders(prev => prev.filter(o => !results.success.includes(o.id)));
    }
  }

  function getStageAccent(status) {
    return stages.find(s => s.key === status)?.accent || colors.textSecondary;
  }

  // Toggle an order in/out of the pick run (persisted so it survives reloads).
  async function toggleExcluded(order) {
    const isExcluded = excluded.has(order.id);
    setExcluded(prev => {
      const next = new Set(prev);
      if (isExcluded) next.delete(order.id); else next.add(order.id);
      return next;
    });
    try {
      if (isExcluded) {
        await supabase.from('excluded_orders').delete().eq('order_id', order.id);
      } else {
        await supabase.from('excluded_orders').upsert({ order_id: order.id, source: order.source }, { onConflict: 'order_id' });
      }
    } catch (err) {
      console.error('Exclusion toggle error:', err);
      setExcluded(prev => {
        const next = new Set(prev);
        if (isExcluded) next.add(order.id); else next.delete(order.id);
        return next;
      });
      alert('Could not update exclusion — see console.');
    }
  }

  // ─── LIST ──────────────────────────────────────────────────────────────────
  if (screen === 'list') {
    const filtered = activeStage === 'all' ? orders : orders.filter(o => o.status === activeStage);
    return (
      <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
        <PageHeader title="Orders" icon="☰" />

        <button onClick={loadOrders} disabled={loading} style={{
          width: '100%', padding: '14px 24px',
          background: loading ? colors.border : colors.navy,
          color: loading ? colors.textSecondary : '#ffffff',
          border: 'none', borderRadius: '6px', fontSize: '1rem', fontWeight: 800,
          fontFamily: "'Outfit', sans-serif", cursor: loading ? 'default' : 'pointer',
          marginBottom: '16px',
        }}>
          {loading ? 'Loading Orders...' : orders.length ? 'Refresh Orders' : 'Load Open Orders'}
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
          {stages.map(stage => {
            const count = orders.filter(o => o.status === stage.key).length;
            const isActive = activeStage === stage.key;
            return (
              <div key={stage.key}
                onClick={() => setActiveStage(activeStage === stage.key ? 'all' : stage.key)}
                style={{
                  background: isActive ? colors.navy : colors.cardBg,
                  border: `1px solid ${isActive ? colors.navy : colors.border}`,
                  borderBottom: `3px solid ${stage.accent}`,
                  borderRadius: '6px', padding: '14px 10px', textAlign: 'center', cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: '1.6rem', fontWeight: 800, color: isActive ? '#ffffff' : stage.accent }}>{count}</div>
                <div style={{ fontSize: '0.68rem', color: isActive ? '#94a3b8' : colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '3px' }}>{stage.key}</div>
              </div>
            );
          })}
        </div>

        <div style={{ marginBottom: '16px' }}>
          <button onClick={runReconcile} disabled={reconciling} style={{
            width: '100%', padding: '11px',
            background: reconciling ? colors.border : colors.cardBg,
            color: reconciling ? colors.textSecondary : colors.navy,
            border: `1px solid ${colors.navy}`, borderRadius: '6px',
            fontSize: '0.85rem', fontWeight: 700, fontFamily: "'Outfit', sans-serif",
            cursor: reconciling ? 'default' : 'pointer',
          }}>
            {reconciling ? 'Checking BrickOwl vs BrickLink…' : '⟳ BrickOwl → BrickLink Sync Check'}
          </button>
          {reconcile && !reconciling && (
            <div style={{ marginTop: '10px', background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '12px 14px' }}>
              {renderReconcilePanel(reconcile, { soldWho: 'BrickOwl', listWho: 'BrickLink', soldKey: 'boSold', listKey: 'blNow', fixWho: 'BrickLink' })}
            </div>
          )}

          <button onClick={runReconcileRev} disabled={reconcilingRev} style={{
            width: '100%', padding: '11px', marginTop: '10px',
            background: reconcilingRev ? colors.border : colors.cardBg,
            color: reconcilingRev ? colors.textSecondary : colors.navy,
            border: `1px solid ${colors.navy}`, borderRadius: '6px',
            fontSize: '0.85rem', fontWeight: 700, fontFamily: "'Outfit', sans-serif",
            cursor: reconcilingRev ? 'default' : 'pointer',
          }}>
            {reconcilingRev ? 'Checking BrickLink vs BrickOwl…' : '⟳ BrickLink → BrickOwl Sync Check'}
          </button>
          {reconcileRev && !reconcilingRev && (
            <div style={{ marginTop: '10px', background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '12px 14px' }}>
              {renderReconcilePanel(reconcileRev, { soldWho: 'BrickLink', listWho: 'BrickOwl', soldKey: 'blSold', listKey: 'boNow', fixWho: 'BrickOwl' })}
            </div>
          )}
        </div>

        {/* Cross-platform auto-deduct control */}
        <div style={{ marginBottom: '16px', background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 800, color: colors.navy, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Auto-deduct on ship
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              {['off', 'preview', 'live'].map(m => (
                <button key={m} onClick={() => changeSyncMode(m)} style={{
                  padding: '5px 12px', fontSize: '0.72rem', fontWeight: 700, fontFamily: "'Outfit', sans-serif",
                  border: `1px solid ${syncMode === m ? (m === 'live' ? colors.greenDark : colors.navy) : colors.border}`,
                  background: syncMode === m ? (m === 'live' ? colors.greenDark : colors.navy) : colors.cardBg,
                  color: syncMode === m ? '#ffffff' : colors.textSecondary,
                  borderRadius: '6px', cursor: 'pointer', textTransform: 'capitalize',
                }}>{m}</button>
              ))}
            </div>
          </div>
          <div style={{ fontSize: '0.7rem', color: colors.textSecondary, marginTop: '8px', lineHeight: 1.4 }}>
            {syncMode === 'off' && 'Off (recommended) — BrickFreedom keeps your two stores in sync, so Brick Audit leaves inventory alone. Leave this Off. Only switch it on as a fallback if BrickFreedom stops working.'}
            {syncMode === 'preview' && 'Preview — when you ship, Brick Audit shows what it WOULD reduce on the other store, but changes nothing. Only useful if BrickFreedom is down.'}
            {syncMode === 'live' && '⚠ Live — Brick Audit reduces the other store on ship. Do NOT use this while BrickFreedom is running, or the same sale gets deducted twice. Fallback only.'}
          </div>
        </div>

        {!loading && orders.length === 0 && (
          <div style={{ background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '12px', color: colors.navy }}>☰</div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: colors.textPrimary, marginBottom: '8px' }}>No orders loaded</div>
            <div style={{ fontSize: '0.82rem', color: colors.textSecondary }}>Tap "Load Open Orders" to pull from BrickLink</div>
          </div>
        )}

        <div
          onClick={() => window.open('https://www.pirateship.com', '_blank')}
          style={{
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            marginBottom: '10px',
          }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: colors.textPrimary }}>Open Pirate Ship ↗</div>
          <div style={{ fontSize: '0.68rem', color: colors.textSecondary }}>Print shipping labels</div>
        </div>

        {savedSession && (
          <div style={{ background: '#f0fdf4', border: `2px solid ${colors.greenDark}`, borderRadius: '8px', padding: '12px 14px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: colors.textPrimary }}>
              💾 You have a saved pick session
              {savedSession.saved_at && <span style={{ color: colors.textSecondary, fontWeight: 400 }}> · {new Date(savedSession.saved_at).toLocaleString()}</span>}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={resumeSession} style={{ padding: '8px 16px', background: colors.greenDark, color: '#fff', border: 'none', borderRadius: '6px', fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer' }}>Resume</button>
              <button onClick={clearSavedSession} style={{ padding: '8px 12px', background: 'none', border: `1px solid ${colors.border}`, borderRadius: '6px', color: colors.textSecondary, fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}>Discard</button>
            </div>
          </div>
        )}

        <button onClick={() => setScreen('custom')} style={{
          width: '100%', padding: '11px', marginBottom: '16px',
          background: colors.cardBg, color: '#7c3aed', border: '1px solid #7c3aed',
          borderRadius: '6px', fontSize: '0.85rem', fontWeight: 700, fontFamily: "'Outfit', sans-serif", cursor: 'pointer',
        }}>
          ＋ Custom Pick{customItems.length > 0 ? ` (${customItems.length} item${customItems.length === 1 ? '' : 's'} ready)` : ''}
        </button>

        {customItems.length > 0 && orders.length > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', fontSize: '0.8rem', color: colors.textPrimary, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeCustom} onChange={e => setIncludeCustom(e.target.checked)} />
            Include Custom Pick ({customItems.reduce((s, i) => s + (i.qty || 0), 0)} pieces) in this run
          </label>
        )}

        {orders.length > 0 && (() => {
          const includedCount = orders.filter(o => !excluded.has(o.id)).length;
          const excludedCount = orders.length - includedCount;
          return (
            <button onClick={startPickRun} disabled={loadingPickRun || includedCount === 0} style={{
              width: '100%', padding: '14px 24px',
              background: (loadingPickRun || includedCount === 0) ? colors.border : colors.greenDark,
              color: includedCount === 0 ? colors.textSecondary : '#ffffff', border: 'none', borderRadius: '6px',
              fontSize: '1rem', fontWeight: 800, fontFamily: "'Outfit', sans-serif",
              cursor: (loadingPickRun || includedCount === 0) ? 'default' : 'pointer', marginBottom: '16px',
            }}>
              {loadingPickRun
                ? 'Building pick list...'
                : includedCount === 0
                  ? 'All orders excluded'
                  : `Start Pick Run — ${includedCount} order${includedCount !== 1 ? 's' : ''}${excludedCount > 0 ? ` (${excludedCount} excluded)` : ''}`}
            </button>
          );
        })()}

        {filtered.map(order => {
          const isExpanded = expandedOrder === order.id;
          const accent = getStageAccent(order.status);
          const items = orderItems[order.id] || [];
          return (
            <div key={order.id} style={{
              background: colors.cardBg, border: `1px solid ${colors.border}`,
              borderLeft: `3px solid ${order.color}`, borderRadius: '6px',
              marginBottom: '10px', overflow: 'hidden',
            }}>
              <div onClick={() => toggleExpand(order.id)} style={{
                padding: '14px 16px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '12px',
                opacity: excluded.has(order.id) ? 0.5 : 1,
              }}>
                <div style={{
                  background: order.color, color: '#ffffff', fontSize: '0.9rem', fontWeight: 900,
                  width: '32px', height: '32px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>{order.letter}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.92rem', fontWeight: 800, color: colors.textPrimary, marginBottom: '2px' }}>{order.buyer}</div>
                  <div style={{ fontSize: '0.7rem', color: colors.textSecondary }}>{order.source === 'brickowl' ? '🦉 ' : ''}{order.id} · {order.itemCount} items · {order.date}</div>
                </div>
                <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <div style={{ fontSize: '1rem', fontWeight: 800, color: colors.textPrimary }}>${order.total}</div>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, color: excluded.has(order.id) ? '#dc2626' : accent, textTransform: 'uppercase', marginTop: '2px' }}>
                    {excluded.has(order.id) ? 'Excluded' : order.status}
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleExcluded(order); }}
                  title={excluded.has(order.id) ? 'Re-include in pick run' : 'Exclude from pick run'}
                  style={{
                    padding: '6px 8px',
                    background: excluded.has(order.id) ? '#fef2f2' : '#f9fafb',
                    border: `1px solid ${excluded.has(order.id) ? '#fecaca' : colors.border}`,
                    borderRadius: '4px',
                    color: excluded.has(order.id) ? '#dc2626' : colors.textSecondary,
                    fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '0.7rem',
                    cursor: 'pointer', flexShrink: 0,
                  }}
                >
                  {excluded.has(order.id) ? '↺ Include' : '⊘ Exclude'}
                </button>
                <div style={{ color: colors.textSecondary, fontSize: '0.8rem', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }}>▼</div>
              </div>

              {isExpanded && (
                <div style={{ borderTop: `1px solid ${colors.border}`, padding: '12px 16px', background: '#f9fafb' }}>
                  {items.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ width: '100%', height: '5px', background: colors.border, borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ width: '100%', height: '100%', background: colors.greenDark, borderRadius: '3px' }} />
                      </div>
                    </div>
                  )}
                  {loadingItems[order.id] && (
                    <div style={{ textAlign: 'center', padding: '20px', color: colors.textSecondary, fontSize: '0.85rem' }}>Loading items...</div>
                  )}
                  {!loadingItems[order.id] && items.map((item, j) => (
                    <div key={j} style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      background: colors.cardBg, border: `1px solid ${colors.border}`,
                      borderRadius: '6px', padding: '10px 12px',
                      marginBottom: j < items.length - 1 ? '6px' : '0',
                    }}>
                      <img src={getImageUrl(item)} alt={item.name}
                        style={{ width: '44px', height: '44px', objectFit: 'contain', background: '#f9fafb', borderRadius: '6px', padding: '3px', border: `1px solid ${colors.border}`, flexShrink: 0 }}
                        onError={e => { e.target.style.opacity = '0.15'; }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: colors.textPrimary, marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                        <div style={{ fontSize: '0.7rem', color: colors.textSecondary }}>{item.partNo} · {item.color}</div>
                      </div>
                      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: 800, color: colors.textPrimary }}>x{item.qty}</div>
                        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: colors.greenDark }}>{item.bin}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ─── CUSTOM PICK BUILDER ────────────────────────────────────────────────────
  if (screen === 'custom') {
    return (
      <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
        <PageHeader title="Custom Pick" icon="＋" />
        <button onClick={() => setScreen('list')} style={{
          marginBottom: '14px', padding: '8px 16px', background: colors.cardBg, border: `1px solid ${colors.border}`,
          borderRadius: '6px', color: colors.textSecondary, fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
        }}>← Back to Orders</button>

        <div style={{ fontSize: '0.72rem', color: colors.textSecondary, marginBottom: '14px', lineHeight: 1.5 }}>
          Build a pull list by hand — parts get matched to your BrickLink bins and walk through the picker like a normal order. Nothing ships; it's just a pick list.
        </div>

        {/* Add one line */}
        <div style={{ background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '12px 14px', marginBottom: '12px' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 800, color: colors.navy, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Add a line</div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <input value={customPart} onChange={e => setCustomPart(e.target.value)} placeholder="Part # (e.g. 3001)"
              style={{ flex: '2 1 120px', padding: '9px 10px', border: `1px solid ${colors.border}`, borderRadius: '6px', fontFamily: "'Outfit', sans-serif", fontSize: '0.82rem', boxSizing: 'border-box' }} />
            <input value={customColor} onChange={e => setCustomColor(e.target.value)} placeholder="Color (optional)"
              style={{ flex: '2 1 120px', padding: '9px 10px', border: `1px solid ${colors.border}`, borderRadius: '6px', fontFamily: "'Outfit', sans-serif", fontSize: '0.82rem', boxSizing: 'border-box' }} />
            <input value={customQty} onChange={e => setCustomQty(e.target.value)} placeholder="Qty" type="number" min="1"
              style={{ flex: '1 1 60px', width: '60px', padding: '9px 10px', border: `1px solid ${colors.border}`, borderRadius: '6px', fontFamily: "'Outfit', sans-serif", fontSize: '0.82rem', boxSizing: 'border-box' }} />
            <button onClick={async () => { const ok = await addCustomLine(customPart, customColor, customQty); if (ok) { setCustomPart(''); setCustomColor(''); setCustomQty('1'); } }}
              style={{ flex: '1 1 60px', padding: '9px 12px', background: colors.navy, color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 700, fontFamily: "'Outfit', sans-serif", fontSize: '0.82rem', cursor: 'pointer' }}>Add</button>
          </div>
        </div>

        {/* Paste many */}
        <div style={{ background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '12px 14px', marginBottom: '12px' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 800, color: colors.navy, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Or paste a list</div>
          <textarea value={customPaste} onChange={e => setCustomPaste(e.target.value)} rows={4}
            placeholder={'One per line, e.g.\n3001 Red 4\n3062pb088, White, 2'}
            style={{ width: '100%', padding: '9px 10px', border: `1px solid ${colors.border}`, borderRadius: '6px', fontFamily: "'Outfit', sans-serif", fontSize: '0.8rem', boxSizing: 'border-box', resize: 'vertical' }} />
          <button disabled={customBusy} onClick={async () => { await addCustomPaste(customPaste); setCustomPaste(''); }}
            style={{ marginTop: '8px', padding: '9px 16px', background: customBusy ? colors.border : colors.navy, color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 700, fontFamily: "'Outfit', sans-serif", fontSize: '0.82rem', cursor: customBusy ? 'default' : 'pointer' }}>
            {customBusy ? 'Adding…' : 'Add all'}
          </button>
          <label style={{ display: 'block', marginTop: '10px', cursor: 'pointer' }}>
            <input type="file" accept=".csv,text/csv" style={{ display: 'none' }}
              onChange={e => { handleCustomCsv(e.target.files[0]); e.target.value = ''; }} />
            <div style={{ border: `2px dashed ${colors.border}`, borderRadius: '6px', padding: '10px', textAlign: 'center', fontSize: '0.75rem', fontWeight: 700, color: colors.navy }}>
              ⬆ Or drop a CSV (Part, Color, Qty)
            </div>
          </label>
        </div>

        {/* Current list */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{customItems.length} item{customItems.length === 1 ? '' : 's'}</div>
          {customItems.length > 0 && <button onClick={clearCustomAll} style={{ background: 'none', border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '3px 10px', fontFamily: "'Outfit', sans-serif", fontSize: '0.68rem', fontWeight: 700, color: '#dc2626', cursor: 'pointer' }}>Clear all</button>}
        </div>
        {customItems.map(it => (
          <div key={it.id} style={{ background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '10px 12px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: colors.textPrimary }}>{it.qty}× {it.part_no}{it.color ? ` · ${it.color}` : ''}</div>
              <div style={{ fontSize: '0.72rem', color: it.matched ? colors.greenDark : '#d97706' }}>
                {it.matched ? `${it.bin || '(no bin)'}` : '⚠ not found in BrickLink inventory — check part # / color'}
              </div>
            </div>
            <button onClick={() => removeCustomItem(it.id)} style={{ background: 'none', border: 'none', color: colors.textSecondary, fontSize: '1.1rem', cursor: 'pointer', padding: '2px 6px' }}>×</button>
          </div>
        ))}

        {customItems.length > 0 && (
          <button onClick={startCustomPick} disabled={loadingPickRun} style={{
            width: '100%', padding: '14px', marginTop: '14px',
            background: loadingPickRun ? colors.border : '#7c3aed', color: '#fff', border: 'none', borderRadius: '6px',
            fontSize: '1rem', fontWeight: 800, fontFamily: "'Outfit', sans-serif", cursor: loadingPickRun ? 'default' : 'pointer',
          }}>{loadingPickRun ? 'Building…' : `Start Custom Pick — ${customItems.reduce((s, i) => s + (i.qty || 0), 0)} pieces`}</button>
        )}
      </div>
    );
  }

  // ─── PICKING ───────────────────────────────────────────────────────────────
  if (screen === 'picking') {
    const currentBinGroup = pickQueue[pickIndex];
    const binItems = currentBinGroup?.items || [];
    const pickedInBin = binItems.filter(i => picked.has(i.pickKey)).length;
    const allBinDone = binItems.length > 0 && pickedInBin === binItems.length;
    const totalPicked = [...picked].length;
    const totalItems = pickQueue.reduce((sum, b) => sum + b.items.length, 0);
    const pct = totalItems ? Math.round(totalPicked / totalItems * 100) : 0;
    const isLastBin = pickIndex >= pickQueue.length - 1;
    const pickRunDone = pickIndex >= pickQueue.length;

    if (pickRunDone && customMode) {
      return (
        <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif", display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '12px', color: '#7c3aed' }}>✓</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#7c3aed', marginBottom: '8px' }}>Custom Pick Complete!</div>
          <div style={{ fontSize: '0.9rem', color: colors.textSecondary, marginBottom: '24px', textAlign: 'center' }}>{totalPicked}/{totalItems} pieces picked · nothing shipped (this isn't a marketplace order)</div>
          <div style={{ fontSize: '0.85rem', color: colors.textPrimary, marginBottom: '16px', fontWeight: 700 }}>Reduce these from BrickLink inventory?</div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button onClick={() => finishCustomPick(true)} style={{
              padding: '14px 24px', background: colors.greenDark, color: '#ffffff',
              border: 'none', borderRadius: '6px', fontSize: '0.95rem', fontWeight: 800,
              fontFamily: "'Outfit', sans-serif", cursor: 'pointer',
            }}>Deduct from BrickLink</button>
            <button onClick={() => finishCustomPick(false)} style={{
              padding: '14px 24px', background: colors.cardBg, color: colors.navy,
              border: `1px solid ${colors.navy}`, borderRadius: '6px', fontSize: '0.95rem', fontWeight: 800,
              fontFamily: "'Outfit', sans-serif", cursor: 'pointer',
            }}>Just finish (no change)</button>
          </div>
          <div style={{ fontSize: '0.68rem', color: colors.textSecondary, marginTop: '14px', maxWidth: '340px', textAlign: 'center' }}>
            Choose "Deduct" only if these pieces are leaving your stock and you are NOT also editing the order on the marketplace (that would remove them twice).
          </div>
        </div>
      );
    }

    if (pickRunDone) {
      return (
        <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif", display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '12px', color: colors.greenDark }}>✓</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: colors.greenDark, marginBottom: '8px' }}>Pick Run Complete!</div>
          <div style={{ fontSize: '0.9rem', color: colors.textSecondary, marginBottom: '24px' }}>{totalPicked}/{totalItems} items picked across {orders.length} orders</div>
          <button onClick={() => setScreen('summary')} style={{
            padding: '14px 32px', background: colors.navy, color: '#ffffff',
            border: 'none', borderRadius: '6px', fontSize: '1rem', fontWeight: 800,
            fontFamily: "'Outfit', sans-serif", cursor: 'pointer',
          }}>View Summary →</button>
        </div>
      );
    }

    return (
      <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
        <div style={{ background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          {customMode ? (
            <span style={{ fontSize: '0.72rem' }}>
              <span style={{ color: '#7c3aed', fontWeight: 800 }}>C</span>
              <span style={{ color: colors.textSecondary }}> Custom Pick</span>
            </span>
          ) : (
            <>
              {orders.filter(o => !excluded.has(o.id)).map(o => (
                <span key={o.id} style={{ fontSize: '0.72rem' }}>
                  <span style={{ color: o.color, fontWeight: 800 }}>{o.letter}</span>
                  <span style={{ color: colors.textSecondary }}> {o.buyer}</span>
                </span>
              ))}
              {customInRun && (
                <span style={{ fontSize: '0.72rem' }}>
                  <span style={{ color: '#7c3aed', fontWeight: 800 }}>C</span>
                  <span style={{ color: colors.textSecondary }}> Custom Pick</span>
                </span>
              )}
            </>
          )}
        </div>

        <div style={{ background: colors.navy, borderLeft: '4px solid #a7ff83', borderRadius: '8px', padding: '20px 24px', marginBottom: '16px' }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#a7ff83', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>
            Pick Mode · Bin {pickIndex + 1} of {pickQueue.length}
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#ffffff', marginBottom: '12px' }}>{currentBinGroup?.bin}</div>
          <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.15)', borderRadius: '3px', overflow: 'hidden', marginBottom: '6px' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: '#a7ff83', borderRadius: '3px', transition: 'width 0.3s ease' }} />
          </div>
          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{totalPicked}/{totalItems} items picked · {pct}%</div>
        </div>

        {allBinDone && (
          <div style={{ background: '#f0fdf4', border: '2px solid #bbf7d0', borderRadius: '8px', padding: '20px', textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '8px', color: colors.greenDark }}>✓</div>
            <div style={{ fontSize: '1rem', fontWeight: 800, color: colors.greenDark, marginBottom: '12px' }}>{currentBinGroup?.bin} complete!</div>
            <button onClick={() => setPickIndex(prev => prev + 1)}
              style={{ padding: '10px 24px', background: colors.navy, color: '#ffffff', border: 'none', borderRadius: '6px', fontSize: '0.9rem', fontWeight: 700, fontFamily: "'Outfit', sans-serif", cursor: 'pointer' }}>
              {isLastBin ? 'View Summary →' : 'Next Bin →'}
            </button>
          </div>
        )}

        {!allBinDone && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', marginBottom: '20px' }}>
            {[...binItems].sort((a, b) => (a.partNo || '').localeCompare(b.partNo || '') || (a.qty || 0) - (b.qty || 0)).map(item => {
              const isPicked = picked.has(item.pickKey);
              return (
                <div key={item.pickKey}
                  onClick={() => setPicked(prev => {
                    const next = new Set(prev);
                    if (next.has(item.pickKey)) next.delete(item.pickKey); else next.add(item.pickKey);
                    return next;
                  })}
                  style={{
                    background: isPicked ? '#f0fdf4' : `${item.orderColor}14`,
                    border: `2.5px solid ${item.orderColor}`,
                    borderRadius: '8px', textAlign: 'center', overflow: 'hidden',
                    cursor: 'pointer', opacity: isPicked ? 0.5 : 1, transition: 'all 0.15s ease',
                  }}
                >
                  {/* Order banner — makes it obvious which order this piece is for */}
                  <div style={{
                    background: item.orderColor, color: '#ffffff',
                    display: 'flex', alignItems: 'center', gap: '5px',
                    padding: '4px 8px', fontSize: '0.66rem', fontWeight: 800,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    textShadow: '0 1px 1px rgba(0,0,0,0.35)', letterSpacing: '0.02em',
                  }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 900 }}>{item.orderLetter}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.orderBuyer}</span>
                  </div>
                  <div style={{ padding: '10px' }}>
                    <div style={{ position: 'relative', marginBottom: '6px' }}>
                      <img src={getImageUrl(item)} alt={item.name}
                        style={{ width: '100%', maxHeight: '80px', objectFit: 'contain' }}
                        onError={e => { e.target.style.opacity = '0.15'; }}
                      />
                      <span style={{ position: 'absolute', top: '2px', left: '4px', fontSize: '1.1rem', fontWeight: 800, color: colors.navy, background: 'rgba(255,255,255,0.85)', padding: '0 4px', borderRadius: '3px', lineHeight: 1.2 }}>x{item.qty}</span>
                      {item.hasOverflow && (
                        <span title="Lot has an overflow bag" style={{ position: 'absolute', bottom: '2px', left: '4px', fontSize: '0.95rem', fontWeight: 800, color: '#d97706', background: 'rgba(255,255,255,0.85)', padding: '0 4px', borderRadius: '3px', lineHeight: 1 }}>⊕</span>
                      )}
                      {item.remaining !== null && item.remaining !== undefined && (
                        <span style={{ position: 'absolute', bottom: '2px', right: '4px', fontSize: '0.7rem', fontWeight: 700, color: colors.textSecondary, background: 'rgba(255,255,255,0.85)', padding: '1px 5px', borderRadius: '3px' }}>{item.remaining} left</span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 800, color: colors.textPrimary }}>{item.partNo}</div>
                    <div style={{ fontSize: '0.6rem', color: colors.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                    <div style={{ fontSize: '0.6rem', color: colors.textSecondary }}>{item.color}</div>
                    {isPicked && <div style={{ marginTop: '4px', color: colors.greenDark, fontWeight: 800, fontSize: '0.8rem' }}>✓ Picked</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => setPickIndex(prev => prev + 1)}
            style={{ flex: 1, padding: '12px', background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '6px', color: colors.textSecondary, fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
            Skip Bin
          </button>
          <button onClick={() => setScreen('summary')}
            style={{ flex: 1, padding: '12px', background: colors.navy, color: '#ffffff', border: 'none', borderRadius: '6px', fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
            Done Picking →
          </button>
        </div>
        <button onClick={() => {
          // Mark every lot in the run as picked and jump to the summary — for
          // when the parts are already physically pulled and tapping each tile
          // would just be busywork.
          const allKeys = pickQueue.flatMap(b => b.items.map(i => i.pickKey));
          setPicked(new Set(allKeys));
          setScreen('fulfill');
        }}
          style={{ width: '100%', marginTop: '10px', padding: '12px', background: colors.greenDark, border: 'none', borderRadius: '6px', color: '#ffffff', fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer' }}>
          ✓ Mark all picked → ship
        </button>
        <button onClick={saveProgress}
          style={{ width: '100%', marginTop: '10px', padding: '12px', background: colors.cardBg, border: `1px solid ${colors.greenDark}`, borderRadius: '6px', color: colors.greenDark, fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
          💾 Save progress &amp; stop — resume later
        </button>
      </div>
    );
  }

  // ─── SUMMARY ───────────────────────────────────────────────────────────────
  if (screen === 'summary') {
    const totalPicked = [...picked].length;
    const totalItems = pickQueue.reduce((sum, b) => sum + b.items.length, 0);

    return (
      <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
        <PageHeader title="Order Summary" icon="☰" />

        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderLeft: `3px solid ${colors.greenDark}`, borderRadius: '6px', padding: '16px', marginBottom: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: colors.greenDark }}>{totalPicked}/{totalItems}</div>
          <div style={{ fontSize: '0.72rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '4px' }}>Items Picked Across All Orders</div>
        </div>

        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>
          Order cards — for filling out inserts
        </div>

        {orders.filter(o => !excluded.has(o.id)).map(order => {
          const items = orderItems[order.id] || [];
          const totalPieces = items.reduce((sum, i) => sum + i.qty, 0);
          const orderPickItems = pickQueue.flatMap(b => b.items).filter(i => i.orderId === order.id);
          const orderPicked = orderPickItems.filter(i => picked.has(i.pickKey)).length;
          const allPicked = orderPickItems.length > 0 && orderPicked === orderPickItems.length;

          return (
            <div key={order.id} style={{ background: colors.cardBg, border: `1px solid ${colors.border}`, borderLeft: `4px solid ${order.color}`, borderRadius: '6px', padding: '16px 20px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                <div style={{ background: order.color, color: '#ffffff', fontSize: '0.9rem', fontWeight: 900, width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{order.letter}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '1rem', fontWeight: 800, color: colors.textPrimary }}>{order.buyer}</div>
                  <div style={{ fontSize: '0.72rem', color: colors.textSecondary }}>Order #{order.id} · {order.date}</div>
                </div>
                <div style={{ color: allPicked ? colors.greenDark : '#d97706', fontWeight: 700, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                  {allPicked ? '✓ All picked' : `${orderPicked}/${orderPickItems.length} picked`}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                {[
                  { label: 'Lots', value: items.length, accent: colors.navy },
                  { label: 'Pieces', value: totalPieces, accent: '#2563eb' },
                  { label: 'Total', value: `$${order.total}`, accent: colors.greenDark },
                ].map((s, i) => (
                  <div key={i} style={{ background: '#f9fafb', border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: 800, color: s.accent }}>{s.value}</div>
                    <div style={{ fontSize: '0.62rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '2px' }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {customInRun && (() => {
          const cItems = pickQueue.flatMap(b => b.items).filter(i => i.orderId === 'custom');
          const cPicked = cItems.filter(i => picked.has(i.pickKey)).length;
          return (
            <div style={{ background: colors.cardBg, border: `1px solid ${colors.border}`, borderLeft: '4px solid #7c3aed', borderRadius: '6px', padding: '16px 20px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ background: '#7c3aed', color: '#fff', fontSize: '0.9rem', fontWeight: 900, width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>C</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '1rem', fontWeight: 800, color: colors.textPrimary }}>Custom Pick</div>
                  <div style={{ fontSize: '0.72rem', color: colors.textSecondary }}>{cItems.reduce((s, i) => s + (i.qty || 0), 0)} pieces · not a marketplace order</div>
                </div>
                <div style={{ color: cPicked === cItems.length ? colors.greenDark : '#d97706', fontWeight: 700, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                  {cPicked === cItems.length ? '✓ All picked' : `${cPicked}/${cItems.length} picked`}
                </div>
              </div>
            </div>
          );
        })()}

        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
          <button onClick={() => window.open('https://www.pirateship.com', '_blank')} style={{ flex: 1, padding: '14px', background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '6px', color: colors.textPrimary, fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
            Open Pirate Ship ↗
          </button>
          <button onClick={() => setScreen('fulfill')} style={{ flex: 1, padding: '14px', background: colors.navy, color: '#ffffff', border: 'none', borderRadius: '6px', fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer' }}>
            Mark Shipped →
          </button>
        </div>
        <button onClick={() => setScreen('picking')} style={{ width: '100%', marginTop: '10px', padding: '12px', background: 'transparent', border: `1px solid ${colors.border}`, borderRadius: '6px', color: colors.textSecondary, fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
          ← Back to Picking
        </button>
      </div>
    );
  }

  // ─── FULFILL ───────────────────────────────────────────────────────────────
  if (screen === 'fulfill') {
    if (fulfillResults) {
      return (
        <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif", display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '12px', color: colors.greenDark }}>✓</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: colors.greenDark, marginBottom: '8px' }}>Orders Shipped!</div>
          <div style={{ fontSize: '0.9rem', color: colors.textSecondary, marginBottom: '8px', textAlign: 'center' }}>
            {fulfillResults.success.length} order(s) marked shipped · Messages sent · Feedback left
          </div>
          {fulfillResults.errors.length > 0 && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '12px 16px', marginBottom: '16px', fontSize: '0.82rem', color: '#dc2626', width: '100%' }}>
              {fulfillResults.errors.map((e, i) => <div key={i}>Order {e.orderId}: {e.error}</div>)}
            </div>
          )}
          {syncResults && syncResults.length > 0 && (() => {
            const allLines = syncResults.flatMap(r => (r.lines || []).map(l => ({ ...l, _src: r.source, _order: r.orderId })));
            const acted = allLines.filter(l => ['applied', 'floored', 'preview'].includes(l.status));
            const floored = allLines.filter(l => l.status === 'floored');
            const errors = allLines.filter(l => l.status === 'error');
            const anyPreview = syncResults.some(r => r.mode === 'preview');
            if (acted.length === 0 && errors.length === 0) return null;
            return (
              <div style={{ background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', width: '100%', maxWidth: '520px', textAlign: 'left' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 800, color: colors.navy, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                  {anyPreview ? 'Sync preview (nothing changed)' : 'Cross-platform sync'}
                </div>
                {acted.map((l, i) => (
                  <div key={i} style={{ fontSize: '0.78rem', color: colors.textPrimary, padding: '3px 0', lineHeight: 1.4 }}>
                    {l.status === 'preview' ? '•' : '✓'} <strong>{l.partNo}</strong>{l.color ? ` · ${l.color}` : ''} — {anyPreview ? 'would reduce' : 'reduced'} {l._src === 'brickowl' ? 'BrickLink' : 'BrickOwl'} by {l.deduct} ({l.current}→{l.newQty})
                    {l.status === 'floored' && <span style={{ marginLeft: '6px', color: '#d97706', fontWeight: 700 }}>floored — was already short</span>}
                  </div>
                ))}
                {floored.length > 0 && (
                  <div style={{ fontSize: '0.7rem', color: '#d97706', marginTop: '6px' }}>
                    ⚠ {floored.length} lot{floored.length > 1 ? 's were' : ' was'} already short on the other store — floored at 0. Worth a manual check.
                  </div>
                )}
                {errors.length > 0 && (
                  <div style={{ fontSize: '0.72rem', color: '#dc2626', marginTop: '6px' }}>
                    {errors.length} sync error{errors.length > 1 ? 's' : ''}: {errors.map(e => `${e.partNo} (${e.detail})`).join('; ')}
                  </div>
                )}
                {anyPreview && (
                  <div style={{ fontSize: '0.68rem', color: colors.textSecondary, marginTop: '8px' }}>
                    This is preview mode. Set "Auto-deduct on ship" to <strong>Live</strong> on the Orders screen to actually apply these.
                  </div>
                )}
              </div>
            );
          })()}
          {customInRun && !customDeductResolved && (
            <div style={{ background: colors.cardBg, border: '1px solid #7c3aed', borderRadius: '8px', padding: '14px 16px', marginBottom: '16px', width: '100%', maxWidth: '520px', textAlign: 'center' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#7c3aed', marginBottom: '4px' }}>Custom Pick was in this run</div>
              <div style={{ fontSize: '0.72rem', color: colors.textSecondary, marginBottom: '10px' }}>Reduce those pieces from BrickLink inventory?</div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={() => resolveCombinedCustom(true)} style={{ padding: '10px 18px', background: colors.greenDark, color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 800, fontFamily: "'Outfit', sans-serif", fontSize: '0.85rem', cursor: 'pointer' }}>Deduct</button>
                <button onClick={() => resolveCombinedCustom(false)} style={{ padding: '10px 18px', background: colors.cardBg, color: colors.navy, border: `1px solid ${colors.navy}`, borderRadius: '6px', fontWeight: 800, fontFamily: "'Outfit', sans-serif", fontSize: '0.85rem', cursor: 'pointer' }}>No, keep stock</button>
              </div>
              <div style={{ fontSize: '0.66rem', color: colors.textSecondary, marginTop: '8px' }}>Deduct only if you're NOT also adding these to the order on the marketplace.</div>
            </div>
          )}
          {customInRun && customDeductResolved && (
            <div style={{ fontSize: '0.75rem', color: colors.greenDark, fontWeight: 700, marginBottom: '12px' }}>✓ Custom pick cleared</div>
          )}
          <button onClick={() => { clearSavedSession(); setScreen('list'); setFulfillResults(null); setSyncResults(null); setOrders([]); setPicked(new Set()); setPickQueue([]); setCustomInRun(false); setCustomDeductResolved(false); setIncludeCustom(false); }}
            style={{ padding: '14px 32px', background: colors.navy, color: '#ffffff', border: 'none', borderRadius: '6px', fontSize: '1rem', fontWeight: 800, fontFamily: "'Outfit', sans-serif", cursor: 'pointer' }}>
            Back to Orders
          </button>
        </div>
      );
    }

    return (
      <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
        <PageHeader title="Mark Shipped" icon="☰" />

        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>
          Enter tracking numbers, review messages, then ship
        </div>

        {/* Bulk tracking import from a shipping-service CSV */}
        <label style={{ display: 'block', marginBottom: '16px', cursor: 'pointer' }}>
          <input type="file" accept=".csv,text/csv" style={{ display: 'none' }}
            onChange={e => { handleTrackingCsv(e.target.files[0]); e.target.value = ''; }} />
          <div style={{ border: `2px dashed ${colors.navy}`, borderRadius: '8px', padding: '14px', textAlign: 'center', background: colors.cardBg }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 800, color: colors.navy }}>⬆ Drop / choose a tracking CSV</div>
            <div style={{ fontSize: '0.68rem', color: colors.textSecondary, marginTop: '4px' }}>
              Fills tracking automatically by matching each order number in the file.
            </div>
          </div>
          {trackingImport && (
            trackingImport.error ? (
              <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#dc2626', fontWeight: 700 }}>{trackingImport.error}</div>
            ) : (
              <div style={{ marginTop: '8px', fontSize: '0.75rem', color: trackingImport.matched > 0 ? colors.greenDark : '#dc2626', fontWeight: 700 }}>
                Matched {trackingImport.matched} of {trackingImport.total} orders
                {trackingImport.unmatched.length > 0 && ` · still need: ${trackingImport.unmatched.join(', ')}`}
              </div>
            )
          )}
        </label>

        {orders.filter(o => !excluded.has(o.id)).map(order => {
          const items = orderItems[order.id] || [];
          const totalPieces = items.reduce((sum, i) => sum + i.qty, 0);
          const buyerName = realNames[order.id] || order.buyer;
          const resolvedMsg = resolveTemplate(DEFAULT_MESSAGE, { buyerName, orderId: order.id, pieces: totalPieces, total: order.total });
          const resolvedFb = resolveTemplate(DEFAULT_FEEDBACK, { buyerName, orderId: order.id, pieces: totalPieces, total: order.total });

          return (
            <div key={order.id} style={{ background: colors.cardBg, border: `1px solid ${colors.border}`, borderLeft: `4px solid ${order.color}`, borderRadius: '6px', padding: '16px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <div style={{ background: order.color, color: '#ffffff', fontSize: '0.85rem', fontWeight: 900, width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{order.letter}</div>
                <div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 800, color: colors.textPrimary }}>{buyerName}</div>
                  <div style={{ fontSize: '0.7rem', color: colors.textSecondary }}>#{order.id} · {totalPieces} pieces · ${order.total}</div>
                </div>
              </div>

              <input type="text" placeholder="Paste tracking number..."
                value={tracking[order.id] || ''}
                onChange={e => setTracking(prev => ({ ...prev, [order.id]: e.target.value }))}
                style={{ width: '100%', padding: '10px 14px', background: '#f9fafb', border: `1px solid ${colors.border}`, borderRadius: '6px', color: colors.textPrimary, fontFamily: "'Outfit', sans-serif", fontSize: '0.85rem', outline: 'none', marginBottom: '12px', boxSizing: 'border-box' }}
              />

              <div style={{ fontSize: '0.65rem', fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px' }}>Drive-thru message</div>
              <div style={{ background: '#f9fafb', border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '10px 12px', fontSize: '0.72rem', color: colors.textPrimary, whiteSpace: 'pre-wrap', lineHeight: 1.6, marginBottom: '8px', maxHeight: '100px', overflowY: 'auto' }}>
                {resolvedMsg}
              </div>

              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderLeft: `3px solid ${colors.greenDark}`, borderRadius: '6px', padding: '8px 12px', fontSize: '0.72rem', color: colors.greenDark }}>
                Feedback: {resolvedFb}
              </div>
            </div>
          );
        })}

        {(() => {
          const includedCount = orders.filter(o => !excluded.has(o.id)).length;
          return (
            <button onClick={fulfillOrders} disabled={fulfilling || includedCount === 0} style={{
              width: '100%', padding: '16px 24px',
              background: (fulfilling || includedCount === 0) ? colors.border : colors.navy,
              color: (fulfilling || includedCount === 0) ? colors.textSecondary : '#ffffff',
              border: 'none', borderRadius: '6px', fontSize: '1rem', fontWeight: 800,
              fontFamily: "'Outfit', sans-serif", cursor: (fulfilling || includedCount === 0) ? 'default' : 'pointer',
              marginBottom: '10px',
            }}>
              {fulfilling
                ? 'Processing...'
                : includedCount === 0
                  ? 'No orders to ship'
                  : `Mark ${includedCount} Shipped + Send Messages + Leave Feedback`}
            </button>
          );
        })()}

        <button onClick={() => setScreen('summary')} style={{ width: '100%', padding: '12px', background: 'transparent', border: `1px solid ${colors.border}`, borderRadius: '6px', color: colors.textSecondary, fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
          ← Back to Summary
        </button>
      </div>
    );
  }

  return null;
}

export default Orders;